import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../helpers/store";
import {
  Backdrop,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  getLoginMethod,
  getLoginToken,
  resetPasswordInit,
  resetSliceState,
  getAuthorizationToken,
  LoginModes,
  redirectToVerifyAccount,
  environmentSettingsInit,
} from "./loginSlice";
import { ErrorCodes } from "../../services/constants";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { emailValidation } from "../../helpers/utils";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { ReducerStates, Views } from "../../helpers/constants";
import { IAlexaData, IAuthorizeData } from "../../services/login.services";
import { StackLogo } from "./StackLogo";
import { handledError } from "../app/appSlice";
import { LoadingIndicator } from "../common/LoadingIndicator";

// Component styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginFormContainer: {
      width: "380px",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      transition: "margin-top 160ms ease",
      [theme.breakpoints.down("sm")]: {
        width: "360px",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    emailLoginFormContainer: {
      marginTop: theme.spacing(5),
      [theme.breakpoints.down("xs")]: {
        marginTop: theme.spacing(3),
      },
    },
    passwordLoginFormContainer: {
      marginTop: theme.spacing(2),
      [theme.breakpoints.down("xs")]: {
        marginTop: theme.spacing(3),
      },
    },
    link: {
      cursor: "pointer",
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 500,
      fontSize: theme.typography.subtitle1.fontSize,
      paddingBottom: theme.spacing(1),
      textAlign: "right",
    },
    textFieldUser: {
      marginTop: theme.spacing(3),
    },
    readOnlyEmailField: {
      "& .MuiOutlinedInput-input": {
        color: theme.palette.text.secondary,
      },
    },
    loginButton: {
      marginTop: theme.spacing(3),
    },
    textFieldPassword: {
      marginTop: "0px",
    },
    selectedEmailPasswordField: {
      marginTop: theme.spacing(3),
    },
    helperText: {
      minHeight: "20px",
      lineHeight: "1.2",
      margin: theme.spacing(1, 0),
    },
    subtitleContainer: {
      display: "none",
      [theme.breakpoints.down("xs")]: {
        display: "block",
        marginTop: theme.spacing(4),
      },
      "& img": {
        maxWidth: "60%",
        margin: "auto",
      },
    },
    linkContainer: {
      display: "flex",
      flexDirection: "column",
      position: "absolute",
      bottom: "30px",
      right: theme.spacing(7),
      [theme.breakpoints.down("xs")]: {
        display: "none",
      },
    },
    buttonRoot: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: "1.1rem !important",
    },
    passwordFallbackButton: {
      marginTop: theme.spacing(1),
      fontSize: theme.typography.body2.fontSize,
      textTransform: "none",
    },
    backButton: {
      marginTop: theme.spacing(1),
      textTransform: "none",
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "#fff",
    },
  })
);

/**
 * Login form component
 */

export default function LoginForm(props: IProps) {
  const showEnvironment = process.env.REACT_APP_ENVIRONMENT !== "production";

  /* ---- Hooks ---- */
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  /* ---- State ---- */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState("password");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordRequired, setPasswordRequired] = useState(
    props.mode === LoginModes.ALEXA
  );
  const [ssoRedirectURL, setSSORedirectURL] = useState("");
  const [canPasswordLogin, setCanPasswordLogin] = useState(false);
  const [isRedirectingSSO, setIsRedirectingSSO] = useState(false);

  /* ---- Selectors ---- */
  const errorCode = useSelector(
    (state: IAppState) => state.loginState.errorCode
  );

  const loginState: ReducerStates = useSelector(
    (state: IAppState) => state.loginState.state
  );

  const isLogged = useSelector((state: IAppState) => state.loginState.isLogged);

  /* ---- References ---- */
  const passwordRef = useRef(document.createElement("input"));

  /* ---- Effects ---- */
  const errorMessage = useSelector(
    (state: IAppState) => state.loginState.errorMessage
  );

  useEffect(() => {
    if (ErrorCodes.hasOwnProperty(errorCode)) {
      switch (ErrorCodes[errorCode]) {
        case ErrorCodes.INVALID_EMAIL_PASSWORD:
          dispatch(handledError());
          setUsernameError(t("email_or_password_is_incorrect"));
          setPasswordError(t("email_or_password_is_incorrect"));
          break;
        case ErrorCodes.UNVALIDATED_ACCOUNT:
          dispatch(handledError());
          dispatch(redirectToVerifyAccount({ email, password }));
          history.push(Views.ACCOUNT_CREATION);
          break;
        case ErrorCodes.SSO_REQUIRED:
          dispatch(handledError());
          setUsernameError(errorMessage || t("sso_required_error"));
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode, t, dispatch]);

  // Manage view flow
  useEffect(() => {
    if (isLogged && ReducerStates[loginState] === ReducerStates.SUCCEEDED) {
      if (props.mode === "login") {
        history.push(Views.DASHBOARD);
        dispatch(resetSliceState());
      }
    }
  }, [isLogged, loginState, dispatch, history, props.mode]);

  useEffect(() => {
    const clearRedirectingSSO = () => {
      setIsRedirectingSSO(false);
    };
    const clearRedirectingSSOIfVisible = () => {
      if (document.visibilityState === "visible") {
        clearRedirectingSSO();
      }
    };

    window.addEventListener("pageshow", clearRedirectingSSO);
    window.addEventListener("focus", clearRedirectingSSO);
    document.addEventListener("visibilitychange", clearRedirectingSSOIfVisible);

    return () => {
      window.removeEventListener("pageshow", clearRedirectingSSO);
      window.removeEventListener("focus", clearRedirectingSSO);
      document.removeEventListener(
        "visibilitychange",
        clearRedirectingSSOIfVisible
      );
    };
  }, []);

  // Set cursor when showing/hiding passwords
  useEffect(() => {
    // Moving cursor to the end
    if (passwordRef.current && passwordRef.current.value) {
      passwordRef.current.selectionStart = passwordRef.current.value.length;
      passwordRef.current.selectionEnd = passwordRef.current.value.length;
    }
  }, [showPassword]);

  // Set cursor when showing/hiding passwords
  useEffect(() => {
    // Moving cursor to the end
    if (passwordRef.current && passwordRef.current.value) {
      passwordRef.current.selectionStart = passwordRef.current.value.length;
      passwordRef.current.selectionEnd = passwordRef.current.value.length;
    }
  }, [showPassword]);

  /* ---- Methods ---- */
  const handleUsernameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { value } = e.target;
    setEmail(value);
    setPassword("");
    setPasswordRequired(props.mode === LoginModes.ALEXA);
    setSSORedirectURL("");
    setCanPasswordLogin(false);
    setIsRedirectingSSO(false);
    setUsernameError("");
    setPasswordError("");
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { value } = e.target;
    setPassword(value);
    setUsernameError("");
    setPasswordError("");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (props.mode === "login" && email && !passwordRequired) {
      if (ssoRedirectURL !== "") {
        setIsRedirectingSSO(true);
        window.location.href = ssoRedirectURL;
        return;
      }

      if (emailValidation(email)) {
        setUsernameError(t("invalid_email"));
        return;
      }

      (dispatch as any)(getLoginMethod(email)).then((action: any) => {
        if (getLoginMethod.fulfilled.match(action)) {
          if (action.payload.auth_type === "password") {
            setSSORedirectURL("");
            setCanPasswordLogin(false);
            setPasswordRequired(true);
            window.requestAnimationFrame(() => passwordRef.current?.focus());
          } else {
            const redirectURL =
              action.payload.sso_login_url || action.payload.idp_sso_url || "";
            if (redirectURL === "") {
              setUsernameError(t("something_happened"));
              return;
            }

            setSSORedirectURL(redirectURL);
            setCanPasswordLogin(action.payload.can_password_login === true);
            setIsRedirectingSSO(false);
          }
        }
      });
      return;
    }

    if (email && password) {
      const loginData = { email, password };
      if (props.mode === "login") {
        dispatch(getLoginToken(loginData));
      } else if (props.mode === LoginModes.ALEXA) {
        if (props.params) {
          const authorizeData: IAuthorizeData = {
            email,
            password,
            ...props.params,
          };
          dispatch(getAuthorizationToken(authorizeData));
        }
      }
    }
  };

  const toggleShowPassword = useCallback(() => {
    setShowPassword((current) =>
      showPassword === "text" ? "password" : "text"
    );
    // Setting focus here
    if (passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [showPassword, passwordRef]);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleForgotPassword = () => {
    dispatch(resetPasswordInit());
  };

  const handleEnvironmentSetting = () => {
    dispatch(environmentSettingsInit());
  };

  const handleBackToEmail = () => {
    setPassword("");
    setShowPassword("password");
    setPasswordRequired(false);
    setSSORedirectURL("");
    setCanPasswordLogin(false);
    setIsRedirectingSSO(false);
    setUsernameError("");
    setPasswordError("");
  };

  /* ---- Utils ---- */
  const validateEmail = () => {
    if (email !== "") {
      if (emailValidation(email)) {
        setUsernameError(t("invalid_email"));
      } else {
        setUsernameError("");
      }
    }
  };

  const buttonText =
    ssoRedirectURL !== "" && !passwordRequired
      ? "login_with_microsoft_entra"
      : props.mode === "login" && !passwordRequired
      ? "continue"
      : props.mode === "login"
      ? "sign_in_title"
      : "authorize";
  const disabledLogin =
    email === "" ||
    (passwordRequired && password === "") ||
    usernameError !== "" ||
    passwordError !== "" ||
    ReducerStates[loginState] === ReducerStates.PENDING ||
    isRedirectingSSO;
  const showReadOnlyEmail =
    props.mode === LoginModes.LOGIN &&
    (passwordRequired || ssoRedirectURL !== "");
  const loginFormStateClass = showReadOnlyEmail
    ? classes.emailLoginFormContainer
    : passwordRequired
    ? classes.passwordLoginFormContainer
    : classes.emailLoginFormContainer;

  return (
    <React.Fragment>
      <StackLogo />
      <form
        id="login-form"
        noValidate
        className={`${classes.loginFormContainer} ${loginFormStateClass}`}
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <FormControl
          variant="outlined"
          fullWidth
          className={`${classes.textFieldUser} ${
            showReadOnlyEmail ? classes.readOnlyEmailField : ""
          }`}
          error={usernameError !== ""}
        >
          <InputLabel htmlFor="username">{t("email_address")}</InputLabel>
          <OutlinedInput
            name="username"
            id="username"
            value={email}
            onChange={handleUsernameChange}
            onBlur={showReadOnlyEmail ? undefined : validateEmail}
            label={t("email_address")}
            autoComplete="off"
            readOnly={showReadOnlyEmail}
          />
          {(!showReadOnlyEmail || usernameError !== "") && (
            <FormHelperText
              id="component-error-text"
              className={classes.helperText}
            >
              {usernameError}
            </FormHelperText>
          )}
        </FormControl>

        {passwordRequired && (
          <FormControl
            variant="outlined"
            fullWidth
            className={`${classes.textFieldPassword} ${
              showReadOnlyEmail ? classes.selectedEmailPasswordField : ""
            }`}
            error={passwordError !== ""}
          >
            <InputLabel htmlFor="component-outlined">
              {t("password")}
            </InputLabel>
            <OutlinedInput
              id="component-outlined"
              name="password"
              type={showPassword}
              inputRef={passwordRef}
              label={t("password")}
              className="password-field"
              value={password}
              onChange={handlePasswordChange}
              autoComplete="current-password"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t("alt_text_toggle_password_visibility")}
                    onClick={toggleShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword === "text" ? (
                      <Visibility />
                    ) : (
                      <VisibilityOff />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        )}
        <Button
          type="submit"
          variant={disabledLogin ? "outlined" : "contained"}
          color="primary"
          size="large"
          disabled={disabledLogin}
          className={classes.loginButton}
          classes={{
            root: classes.buttonRoot,
          }}
        >
          {t(buttonText)}
        </Button>
        {ssoRedirectURL !== "" && canPasswordLogin && !passwordRequired && (
          <Button
            className={classes.passwordFallbackButton}
            color="primary"
            onClick={() => {
              setPasswordRequired(true);
              window.requestAnimationFrame(() => passwordRef.current?.focus());
            }}
            size="small"
            type="button"
          >
            {t("login_with_password")}
          </Button>
        )}
        {showReadOnlyEmail && (
          <Button
            className={classes.backButton}
            color="primary"
            onClick={handleBackToEmail}
            size="small"
            type="button"
          >
            {t("back")}
          </Button>
        )}
      </form>
      <Backdrop className={classes.backdrop} open={isRedirectingSSO}>
        <LoadingIndicator />
      </Backdrop>
      {props.mode === LoginModes.ALEXA && (
        <div className={classes.subtitleContainer}>
          {props.subtitleComponent}
        </div>
      )}
      <div className={classes.linkContainer}>
        <Link
          className={classes.link}
          onClick={handleForgotPassword}
          color="primary"
          to={Views.RESET_PASSWORD}
          component={RouterLink}
        >
          <span>{t("forgot_password")}</span>
        </Link>
        <Link
          className={classes.link}
          onClick={() => undefined}
          color="primary"
          to={Views.ACCOUNT_CREATION}
          component={RouterLink}
        >
          <span>{`${t("new_user")}? >`}</span>
        </Link>
        {showEnvironment && (
          <Link
            className={classes.link}
            onClick={handleEnvironmentSetting}
            color="primary"
            to={Views.ENVIRONMENT_SETTING}
            component={RouterLink}
          >
            <span>{"Environment Settings >"}</span>
          </Link>
        )}
      </div>
    </React.Fragment>
  );
}

interface IProps {
  mode: string;
  subtitleComponent: React.ReactNode;
  params?: IAlexaData;
}
